import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    WarningOutlined,
    DownloadOutlined,
    ArrowLeftOutlined,
    LoadingOutlined,
    PlusOutlined,
    DeleteOutlined,
    SyncOutlined,
    LockOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { App, Tag } from "antd";
import { formatRowsForExcel, applyExcelNumberFormat } from "../../utils/downloadUtils";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
    fetchSimulationDetails,
    fetchSimulationConfig,
    fetchContainers,
    fetchPCS,
    fetchContainerPerformances,
    saveSimulation,
    runSimulation,
} from "../../services/bessApi";

export default function BessSizingResultPage({ hiddenReportOnlyMode = false, simulationId = null }) {
    const params = useParams();
    const id = simulationId || params.id;
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [results, setResults] = useState(null);
    const [containers, setContainers] = useState([]);
    const [pcsList, setPcsList] = useState([]);
    const [performances, setPerformances] = useState([]);
    const [generatingReport, setGeneratingReport] = useState(false);
    const [editablePurchaseYears, setEditablePurchaseYears] = useState([]);
    const [updatingAugmentation, setUpdatingAugmentation] = useState(false);
    const [minAugPeriod, setMinAugPeriod] = useState("");
    const [minAugContainer, setMinAugContainer] = useState("");
    const [maxStorageCapacity, setMaxStorageCapacity] = useState("");

    const loadData = async () => {
        try {
            setLoading(true);
            const [configData, resultsData, containersData, pcsListData] = await Promise.all([
                fetchSimulationConfig(id),
                fetchSimulationDetails(id),
                fetchContainers(),
                fetchPCS(),
            ]);
            setConfig(configData);
            setResults(resultsData);
            setContainers(containersData || []);
            setPcsList(pcsListData || []);

            if (configData) {
                const rawYears = configData.purchase_years || [];
                const parsedYears = rawYears.map((item) => {
                    if (typeof item === "object" && item !== null) {
                        return {
                            year: item.year !== undefined ? Number(item.year) : 1,
                            containers: item.containers !== undefined ? Number(item.containers) : 1,
                        };
                    }
                    return { year: Number(item) || 1, containers: 1 };
                });
                setEditablePurchaseYears(parsedYears);
                setMinAugPeriod(configData.min_aug_period ?? "");
                setMinAugContainer(configData.min_aug_container ?? "");
                setMaxStorageCapacity(configData.max_storage_capacity ?? "");
            }

            if (configData && configData.container_batch) {
                const perfs = await fetchContainerPerformances(configData.container_batch);
                setPerformances(perfs || []);
            }
        } catch (err) {
            console.error("Failed to load simulation results:", err);
            message.error(err.message || "Failed to load simulation results.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateAndReRun = async () => {
        if (config?.is_frozen) {
            message.error("This simulation is frozen and cannot be edited or re-run.");
            return;
        }

        if (config?.simulation_type === "closed_loop") {
            if (!editablePurchaseYears || editablePurchaseYears.length === 0) {
                message.error("Please add at least one augmentation entry before re-running.");
                return;
            }

            const tenure = Number(config?.simulation_tenure) || 50;
            for (let i = 0; i < editablePurchaseYears.length; i++) {
                const item = editablePurchaseYears[i];
                const yr = Number(item.year);
                const ctrs = Number(item.containers);
                if (!yr || isNaN(yr) || yr < 1 || yr > tenure) {
                    message.error(`Augmentation #${i + 1}: Year must be a number between 1 and ${tenure}.`);
                    return;
                }
                if (!ctrs || isNaN(ctrs) || ctrs < 1) {
                    message.error(`Augmentation #${i + 1}: Container quantity must be at least 1.`);
                    return;
                }
            }
        }

        if (config?.simulation_type === "auto_closed_loop") {
            if (minAugPeriod === "" || minAugContainer === "" || maxStorageCapacity === "") {
                message.error("Please fill all auto augmentation parameters.");
                return;
            }
        }

        try {
            setUpdatingAugmentation(true);
            const payload = {
                ...config,
                purchase_years: editablePurchaseYears,
                min_aug_period: minAugPeriod !== "" ? Number(minAugPeriod) : null,
                min_aug_container: minAugContainer !== "" ? Number(minAugContainer) : null,
                max_storage_capacity: maxStorageCapacity !== "" ? Number(maxStorageCapacity) : null,
            };
            await saveSimulation(payload);
            await runSimulation(config.id);
            message.success("Augmentation schedule saved! Re-running sizing simulation...");
            await loadData();
        } catch (err) {
            console.error("Failed to update and re-run simulation:", err);
            message.error(err.message || "Failed to update and re-run simulation.");
        } finally {
            setUpdatingAugmentation(false);
        }
    };

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    // Helper to calculate degradation factor (combines SOH and calendar degradation)
    const getDegradationFactor = (perf) => {
        if (!perf) return 1.0;
        const soh = parseFloat(perf.soh) / 100.0;
        const calVal = parseFloat(perf.calander_degradation);
        let calFactor = 1.0;
        if (calVal > 1.0) {
            calFactor = calVal / 100.0;
        } else if (calVal === 1.0 || calVal === 0) {
            calFactor = 1.0;
        } else {
            calFactor = 1.0 - (calVal / 100.0);
        }
        return soh * calFactor;
    };

    // Helper for ordinals (e.g. 3rd, 10th)
    const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const sortedCohorts = [...(results?.cohorts || [])].sort((a, b) => a.cohort_number - b.cohort_number);
    const selectedContainer = containers.find(c => c.id === config?.container_batch);
    const ratedCapacity = parseFloat(selectedContainer?.rated_capacity_mwh) || 0;

    // Ensure we have at least 3 cohorts defined (even if empty) to match the reference layout structure:
    const cohortsToRender = [];
    const numCohorts = Math.max(3, sortedCohorts.length);
    for (let i = 0; i < numCohorts; i++) {
        const c = sortedCohorts[i];
        if (i === 0) {
            cohortsToRender.push({
                index: 0,
                exists: !!c,
                cohort: c,
                header: "Base Case (Initial Installation)"
            });
        } else {
            cohortsToRender.push({
                index: i,
                exists: !!c,
                cohort: c,
                header: c
                    ? `Auguendation-${i} @ ${getOrdinal(c.purchase_project_year - 1)} Year`
                    : `Auguendation-${i}`
            });
        }
    }

    const handleDownloadExcel = () => {
        if (!results || !results.year_outputs) return;

        // Construct headers
        const headers = [];
        cohortsToRender.forEach(cItem => {
            headers.push(cItem.header);
            headers.push("MWh");
        });
        headers.push("Total Container");
        headers.push("Year");
        headers.push("DC installed capacity");
        headers.push("Calander degradation for 6 months at 25 deg C");
        headers.push("SOH (%)");
        headers.push("Y-O-Y degradation");
        headers.push("DOD");

        cohortsToRender.forEach(cItem => {
            if (cItem.index === 0) {
                headers.push("DC Usable capacity@ Container terminals (MWh)");
            } else {
                headers.push(`DC Usable capacity@Aumendation-${cItem.index} (MWh)`);
            }
        });

        headers.push("Total DC Usable capacity (MWh)");
        headers.push("DC Discharge Efficiency (%)");
        headers.push("DC RTE (%)");
        headers.push("DC Charge Efficiency (%)");
        headers.push("Aux Power Losses (MWh)");
        headers.push("Usable Power after Aux power consumption (MWh)");
        headers.push("Total % losses till POI ( DC disccharge + System losses + Unavailbility)");
        headers.push("Battery power to Grid (POI) MWh");
        headers.push("Grid Import for BESS Charging (MWh)");

        // Construct rows
        const rows = results.year_outputs.map((yr) => {
            const projectYear = yr.project_year;
            const y = projectYear - 1;
            const perf = performances.find(p => p.year === y);

            const activeAfterAux = parseFloat(yr.active_fleet.after_aux_mwh);
            const totalLossesPct = activeAfterAux > 0
                ? (parseFloat(yr.active_fleet.poi_mwh) / activeAfterAux) * 100
                : 0;

            const rowData = [];

            // Cohort columns: Count and capacity
            cohortsToRender.forEach(cItem => {
                if (!cItem.exists) {
                    rowData.push("");
                    rowData.push("");
                    return;
                }
                const age = projectYear - cItem.cohort.purchase_project_year;
                if (age < 0) {
                    rowData.push("");
                    rowData.push("");
                } else {
                    rowData.push(cItem.cohort.containers_purchased);
                    rowData.push(parseFloat((cItem.cohort.containers_purchased * ratedCapacity).toFixed(2)));
                }
            });

            // Total Container
            rowData.push(yr.active_fleet.containers);

            // Year
            rowData.push(projectYear);

            // DC installed capacity
            rowData.push(parseFloat(parseFloat(yr.active_fleet.dc_installed_mwh).toFixed(2)));

            // Calendar degradation
            rowData.push(perf ? `${parseFloat(perf.calander_degradation).toFixed(2)}%` : "100.00%");

            // SOH (%)
            rowData.push(perf ? `${parseFloat(perf.soh).toFixed(2)}%` : "");

            // Y-O-Y degradation
            rowData.push(perf ? `${parseFloat(perf.yoy_degradation).toFixed(2)}%` : "");

            // DOD
            rowData.push(perf ? `${parseFloat(perf.dod).toFixed(2)}%` : "100.00%");

            // Usable capacity for cohorts
            cohortsToRender.forEach(cItem => {
                if (!cItem.exists) {
                    rowData.push("");
                    return;
                }
                const age = projectYear - cItem.cohort.purchase_project_year;
                if (age < 0) {
                    rowData.push("");
                } else {
                    const cPerf = performances.find(p => p.year === age);
                    const cFactor = getDegradationFactor(cPerf);
                    const usableCap = cItem.cohort.containers_purchased * ratedCapacity * cFactor;
                    rowData.push(parseFloat(usableCap.toFixed(5)));
                }
            });

            // Total DC Usable capacity
            rowData.push(parseFloat(parseFloat(yr.active_fleet.terminals_mwh).toFixed(5)));

            // DC Discharge Efficiency
            rowData.push(perf ? `${parseFloat(perf.discharge_efficiency).toFixed(2)}%` : "");

            // DC RTE
            rowData.push(perf ? `${parseFloat(perf.dc_rte).toFixed(2)}%` : "");

            // DC Charge Efficiency
            rowData.push(perf ? `${parseFloat(perf.charge_efficiency).toFixed(2)}%` : "");

            // Aux Power Losses
            rowData.push(parseFloat(parseFloat(yr.active_fleet.aux_mwh).toFixed(2)));

            // Usable Power after Aux
            rowData.push(parseFloat(activeAfterAux.toFixed(2)));

            // Total % losses till POI
            rowData.push(`${totalLossesPct.toFixed(2)}%`);

            // Battery power to Grid
            rowData.push(parseFloat(parseFloat(yr.active_fleet.poi_mwh).toFixed(2)));

            // Grid Import for BESS Charging
            rowData.push(parseFloat(parseFloat(yr.active_fleet.grid_import_for_bess_chg || 0).toFixed(2)));

            return rowData;
        });

        const aoaData = [headers, ...formatRowsForExcel(rows)];
        const ws = XLSX.utils.aoa_to_sheet(aoaData);
        applyExcelNumberFormat(ws);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Simulation Outputs");
        const cleanSimName = config?.name ? config.name.trim().replace(/[^a-zA-Z0-9_-]/g, '_') : 'Unnamed';
        const simId = results?.simulation_id || id;
        
        XLSX.writeFile(wb, `BESS_Sizing_Simulation_${cleanSimName}_${simId}_Outputs.xlsx`);
    };

    // selectedContainer is already declared above
    const selectedPCS = pcsList.find(p => p.id === config?.pcs);

    const degradationTableRows = (() => {
        if (!results || !results.year_outputs) return [];

        const lineLoss220 = config?.use_220kv_loss !== false && (config?.transmission_line_220kv_loss ?? config?.transmission_line_loss) != null ? parseFloat(config.transmission_line_220kv_loss ?? config.transmission_line_loss) : 0;
        const lineLoss400 = (config?.use_400kv_loss === true || (config?.transmission_line_400kv_loss != null && config?.transmission_line_400kv_loss !== ""))
            ? parseFloat(config.transmission_line_400kv_loss ?? 0)
            : 0;
        const t220 = (config?.transformer_220_loss ?? config?.transformer_220kv_loss) != null
            ? parseFloat(config.transformer_220_loss ?? config.transformer_220kv_loss)
            : (config?.pss_transformer_loss ? parseFloat(config.pss_transformer_loss) : 0);
        const t400 = (config?.use_transformer_400_loss === true || (config?.transformer_400_220_loss != null && config?.transformer_400_220_loss !== "") || (config?.transformer_400_loss != null && config?.transformer_400_loss !== ""))
            ? parseFloat(config.transformer_400_220_loss ?? config.transformer_400_loss ?? 0)
            : 0;
        const htLoss = parseFloat(config?.ht_ac_cable_loss) || 0;
        const idtLoss = parseFloat(config?.idt_loss) || 0;
        const ltLoss = parseFloat(config?.lt_ac_cable_loss) || 0;
        const pcsLoss = parseFloat(config?.pcs_loss) || 0;
        const ltdcLoss = parseFloat(config?.lt_dc_cable_loss) || 0;
        const plantUnavail = parseFloat(config?.bess_plant_unavailability) || 0;
        const solarAuxPower = parseFloat(config?.solar_aux_power) || 0;

        const bosEff = (1 - lineLoss220 / 100) *
            (1 - lineLoss400 / 100) *
            (1 - t220 / 100) *
            (1 - t400 / 100) *
            (1 - htLoss / 100) *
            (1 - idtLoss / 100) *
            (1 - ltLoss / 100) *
            (1 - pcsLoss / 100) *
            (1 - ltdcLoss / 100) *
            (1 - plantUnavail / 100) *
            (1 - solarAuxPower / 100);

        const initialDCInstalled = parseFloat(config?.container_nos) * (parseFloat(selectedContainer?.rated_capacity_mwh) || 0);

        const rows = [];

        // Year 0
        const perf0 = performances.find(p => p.year === 0);
        const rte0 = perf0 ? parseFloat(perf0.dc_rte) : 94.96;
        const installed0 = initialDCInstalled * 1000;
        const poi0 = installed0 * (rte0 / 100) * bosEff;

        rows.push({
            year: 0,
            soh: 100.0,
            installed: installed0,
            rte: rte0,
            poi: poi0
        });

        // Years 1 to N
        results.year_outputs.forEach((yr) => {
            const y = yr.project_year;
            const perf = performances.find(p => p.year === y);
            const rte = perf ? parseFloat(perf.dc_rte) : 94.5;

            const term = parseFloat(yr.initial_plant.terminals_mwh) || 0;
            const dcInst = parseFloat(yr.initial_plant.dc_installed_mwh) || 1;
            const soh = (term / dcInst) * 100;

            rows.push({
                year: y,
                soh: soh,
                installed: term * 1000,
                rte: rte,
                poi: parseFloat(yr.initial_plant.poi_mwh) * 1000
            });
        });

        return rows;
    })();

    const sohData = degradationTableRows.map(r => ({
        year: r.year,
        soh: r.soh
    }));

    const handleDownloadReport = async () => {
        setGeneratingReport(true);
        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageIds = ['bess-report-page-1', 'bess-report-page-2', 'bess-report-page-3', 'bess-report-page-4', 'bess-report-page-5'];

            for (let i = 0; i < pageIds.length; i++) {
                const pageEl = document.getElementById(pageIds[i]);
                if (!pageEl) continue;

                const canvas = await html2canvas(pageEl, {
                    scale: 2.2,
                    useCORS: true,
                    logging: false,
                    scrollY: 0,
                    scrollX: 0,
                });

                const imgData = canvas.toDataURL('image/png');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

                if (i < pageIds.length - 1) {
                    pdf.addPage();
                }
            }

            const cleanSimName = config?.name ? config.name.trim().replace(/[^a-zA-Z0-9_-]/g, '_') : 'Unnamed';
            const simId = results?.simulation_id || id;
            pdf.save(`BESS_Sizing_Simulation_${cleanSimName}_${simId}_Report.pdf`);
        } catch (err) {
            console.error('BESS Simulation Report PDF Generation failed:', err);
            message.error('Failed to generate PDF report.');
        } finally {
            setGeneratingReport(false);
        }
    };

    const handleLoadConfig = () => {
        if (config) {
            navigate("/bess-sizing", { state: { loadSim: config } });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col justify-center items-center bg-[#f7f9fb] text-slate-600">
                <LoadingOutlined style={{ fontSize: 32 }} className="text-[#0052cc] mb-4" />
                <span className="text-sm font-semibold">Loading Simulation results...</span>
            </div>
        );
    }

    if (!config || !results) {
        return (
            <div className="min-h-screen flex flex-col justify-center items-center bg-[#f7f9fb] text-slate-600">
                <WarningOutlined style={{ fontSize: 32 }} className="text-red-500 mb-4" />
                <span className="text-sm font-semibold mb-4">Simulation results not found.</span>
                <button
                    type="button"
                    onClick={() => navigate("/bess-sizing")}
                    className="h-10 px-5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                >
                    <ArrowLeftOutlined /> Back to Sizing Builder
                </button>
            </div>
        );
    }

    const typeLabel = config.simulation_type === "closed_loop"
        ? "Manual Battery Augmentation"
        : config.simulation_type === "auto_closed_loop"
        ? "Auto Battery Augmentation"
        : "Without Battery Augmentation";

    const displayName = config.name ? config.name : `Simulation #${config.id}`;

    return (
        <div className={hiddenReportOnlyMode ? "bess-hidden-wrapper" : "bess-container-redesign min-h-screen pt-2 px-6 pb-6 md:pt-4 md:px-8 md:pb-8 flex flex-col relative z-0"}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,100..900;1,100..900&display=swap');

                .bess-container-redesign {
                    background-color: #f7f9fb !important;
                    font-family: 'Inter', sans-serif !important;
                    color: #191c1e !important;
                }

                .bess-container-redesign button,
                .bess-container-redesign input,
                .bess-container-redesign select,
                .bess-container-redesign textarea {
                    font-family: 'Inter', sans-serif !important;
                }

                .bess-container-redesign h1,
                .bess-container-redesign h2,
                .bess-container-redesign h3,
                .bess-container-redesign h4 {
                    font-family: 'Inter', sans-serif !important;        
                    font-weight: 600 !important;
                    border-left: none !important;
                    padding-left: 0 !important;
                    text-transform: none !important;
                    margin: 0 !important;
                    letter-spacing: -0.01em !important;
                }

                .bess-container-redesign .headline-lg {
                    font-size: 30px !important;
                    line-height: 38px !important;
                    letter-spacing: -0.02em !important;
                }

                .bess-card {
                    background-color: #ffffff !important;
                    border-radius: 10px !important;
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02) !important;
                    padding: 32px !important;
                    border: 1px solid #eceef0 !important;
                    box-sizing: border-box !important;
                }

                .geist-val {
                    font-family: 'Inter', sans-serif !important;
                    font-weight: 500 !important;
                }

                .bess-table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                }

                .bess-table th {
                    background-color: #f8fafc !important;
                    font-family: 'Inter', sans-serif !important;
                    font-size: 11px !important;
                    font-weight: 500 !important;
                    color: #707881 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 10px 16px !important;
                    border-bottom: 1px solid #e6e8ea !important;
                    position: sticky !important;
                    top: 0 !important;
                    z-index: 10 !important;
                }

                .bess-table td {
                    padding: 12px 16px !important;
                    border-bottom: 1px solid #f2f4f6 !important;
                    font-size: 13.5px !important;
                    color: #191c1e !important;
                }
            `}</style>

            {!hiddenReportOnlyMode && (
            <div className="max-w-[1280px] mx-auto w-full flex-1">
                {/* Header Back & Title Block */}
                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-[#bfc7d2] pb-4 gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/bess-sizing")}
                            className="h-9 px-4 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                        >
                            <ArrowLeftOutlined /> Back
                        </button>
                        <h1 className="headline-lg font-bold text-[#0c2340]">
                            {displayName}
                        </h1>
                        {config?.is_frozen && (
                            <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-cyan-50 text-cyan-700 border-cyan-200 flex items-center gap-1">
                                <LockOutlined style={{ fontSize: '11px' }} /> Frozen
                            </span>
                        )}
                        {/* <span className="text-xs font-semibold text-slate-500 px-2.5 py-0.5 rounded bg-slate-100">{typeLabel} Results</span> */}
                    </div>
                    <div className="flex items-center gap-2.5 self-start md:self-auto">
                        <button
                            type="button"
                            onClick={handleLoadConfig}
                            className="h-9 px-4 border border-blue-500 text-blue-600 bg-white hover:bg-blue-50/50 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                        >
                            Load Config
                        </button>
                        <button
                            type="button"
                            onClick={handleDownloadReport}
                            disabled={generatingReport}
                            className="h-9 px-4 bg-[#0052cc] hover:bg-[#0043a4] text-white rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {generatingReport ? <LoadingOutlined /> : <DownloadOutlined />} Download Report
                        </button>
                    </div>
                </div>

                {(() => {
                    const lineLoss220 = config?.use_220kv_loss !== false && (config?.transmission_line_220kv_loss ?? config?.transmission_line_loss) != null
                        ? parseFloat(config.transmission_line_220kv_loss ?? config.transmission_line_loss)
                        : 0;
                    const lineLoss400 = config?.transmission_line_400kv_loss != null
                        ? parseFloat(config.transmission_line_400kv_loss)
                        : 0;
                    const t220 = (config?.transformer_220kv_loss ?? config?.transformer_220_loss) != null
                        ? parseFloat(config.transformer_220kv_loss ?? config.transformer_220_loss)
                        : (config?.pss_transformer_loss ? parseFloat(config.pss_transformer_loss) : 0);
                    const t400 = (config?.transformer_400_220_loss ?? config?.transformer_400kv_loss ?? config?.transformer_400_loss) != null
                        ? parseFloat(config.transformer_400_220_loss ?? config.transformer_400kv_loss ?? config.transformer_400_loss)
                        : 0;
                    const htLoss = parseFloat(config?.ht_ac_cable_loss) || 0;
                    const idtLoss = parseFloat(config?.idt_loss) || 0;
                    const ltLoss = parseFloat(config?.lt_ac_cable_loss) || 0;
                    const pcsLoss = parseFloat(config?.pcs_loss) || 0;
                    const ltdcLoss = parseFloat(config?.lt_dc_cable_loss) || 0;
                    const plantUnavail = parseFloat(config?.bess_plant_unavailability) || 0;
                    const solarAuxPower = parseFloat(config?.solar_aux_power) || 0;

                    const bosEff = (1 - lineLoss220 / 100) *
                        (1 - lineLoss400 / 100) *
                        (1 - t220 / 100) *
                        (1 - t400 / 100) *
                        (1 - htLoss / 100) *
                        (1 - idtLoss / 100) *
                        (1 - ltLoss / 100) *
                        (1 - pcsLoss / 100) *
                        (1 - ltdcLoss / 100) *
                        (1 - plantUnavail / 100) *
                        (1 - solarAuxPower / 100);
                    const cumulativeLossPct = (1 - bosEff) * 100;

                    const containerObj = containers.find(c => c.id === (typeof config.container_batch === 'object' ? config.container_batch?.id : config.container_batch));
                    const pcsObj = pcsList.find(p => p.id === (typeof config.pcs === 'object' ? config.pcs?.id : config.pcs));

                    const selectedContainerName = config.container_batch_name || (containerObj
                        ? `${containerObj.oem} (${parseFloat(containerObj.rated_capacity_mwh || 0).toFixed(2)} MWh)`
                        : (config.container_batch ? `Container #${config.container_batch}` : "—"));

                    const selectedPcsName = config.pcs_name || (pcsObj
                        ? `${pcsObj.oem}${pcsObj.model ? ` - ${pcsObj.model}` : ''}`
                        : (config.pcs ? `PCS #${config.pcs}` : "—"));

                    return (
                        <div className="space-y-6">
                            {/* Summary Parameters Card */}
                            <div className="border border-slate-200 rounded-2xl p-5 bg-white shadow-sm space-y-4">
                                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                    <h4 className="font-bold text-[#0052CC] text-xs uppercase tracking-wider">
                                        Simulation Configurations Overview
                                    </h4>
                                    <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">
                                        {typeLabel}
                                    </span>
                                </div>

                                {/* Primary Parameters Grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 text-xs bg-[#f8fafc] border border-slate-100 rounded-xl p-3.5">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Selected Container</span>
                                        <span className="font-bold text-slate-800 text-xs truncate" title={selectedContainerName}>{selectedContainerName}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Selected PCS Model</span>
                                        <span className="font-bold text-slate-800 text-xs truncate" title={selectedPcsName}>{selectedPcsName}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Tenure</span>
                                        <span className="font-bold text-slate-800">{config.simulation_tenure} Years</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Commence Year</span>
                                        <span className="font-bold text-slate-800">{config.start_year}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">POI Target</span>
                                        <span className="font-bold text-slate-800">{Number(config.energy_poi_mwh).toFixed(1)} MWh</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Initial Containers</span>
                                        <span className="font-bold text-slate-800">{config.container_nos} Ctrs</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Initial PCS</span>
                                        <span className="font-bold text-slate-800">{config.pcs_nos} Nos</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Storage Duration</span>
                                        <span className="font-bold text-slate-800">{config.operation_hour} Hours</span>
                                    </div>
                                </div>

                                {/* System Losses Summary Section */}
                                <div className="pt-1">
                                    <div className="flex justify-between items-center mb-2.5">
                                        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                                            System Losses Summary
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 text-xs">
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">{config?.tl_volt_stage_1 ?? 220} kV Line Loss</span>
                                            <span className="font-bold text-slate-800">{lineLoss220.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">{config?.tl_volt_stage_1 ?? 220}/33 kV Transformer Loss</span>
                                            <span className="font-bold text-slate-800">{t220.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">{config?.tl_volt_stage_2 ?? 400} kV Line Loss</span>
                                            <span className="font-bold text-slate-800">{lineLoss400.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">{config?.tl_volt_stage_2 ?? 400}/{config?.tl_volt_stage_1 ?? 220} kV Transformer Loss</span>
                                            <span className="font-bold text-slate-800">{t400.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">HT AC Cable Loss</span>
                                            <span className="font-bold text-slate-800">{htLoss.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">ID Transformer Loss</span>
                                            <span className="font-bold text-slate-800">{idtLoss.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">LT AC Cable Loss</span>
                                            <span className="font-bold text-slate-800">{ltLoss.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">PCS Loss</span>
                                            <span className="font-bold text-slate-800">{pcsLoss.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">LT DC Cable Loss</span>
                                            <span className="font-bold text-slate-800">{ltdcLoss.toFixed(2)}%</span>
                                        </div>
                                        <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                            <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">Plant Availability</span>
                                            <span className="font-bold text-slate-800">{plantUnavail.toFixed(2)}%</span>
                                        </div>
                                        {solarAuxPower > 0 && (
                                            <div className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                                <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">Solar Aux Power (kW)</span>
                                                <span className="font-bold text-slate-800">{solarAuxPower.toFixed(2)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Auto Augmentation Settings */}
                            {config?.simulation_type === "auto_closed_loop" && (
                                <div className="border border-slate-200 rounded-2xl p-5 bg-white shadow-sm space-y-4 mt-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-3">
                                        <div>
                                            <h4 className="font-bold text-[#0052CC] text-xs uppercase tracking-wider">
                                                Auto Augmentation Parameters
                                            </h4>
                                            <p className="text-[11px] text-slate-500 mt-0.5">
                                                Edit augmentation limits below to re-run and compare updated results.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleUpdateAndReRun}
                                            disabled={updatingAugmentation || config?.is_frozen}
                                            className="h-9 px-4 bg-[#0052cc] hover:bg-[#0043a4] text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed self-start sm:self-auto"
                                            title={config?.is_frozen ? "Simulation is frozen" : ""}
                                        >
                                            {updatingAugmentation ? <LoadingOutlined /> : <SyncOutlined />} Re-run Simulation
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[11px] font-bold text-slate-500 uppercase">Min Aug Period (Years)</label>
                                            <input
                                                type="number"
                                                value={minAugPeriod}
                                                disabled={config?.is_frozen}
                                                onChange={(e) => setMinAugPeriod(e.target.value === "" ? "" : Number(e.target.value))}
                                                className="h-9 px-3 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-blue-500 bg-white"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[11px] font-bold text-slate-500 uppercase">Min Aug Containers (Nos)</label>
                                            <input
                                                type="number"
                                                value={minAugContainer}
                                                disabled={config?.is_frozen}
                                                onChange={(e) => setMinAugContainer(e.target.value === "" ? "" : Number(e.target.value))}
                                                className="h-9 px-3 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-blue-500 bg-white"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[11px] font-bold text-slate-500 uppercase">Max Storage Capacity (%)</label>
                                            <input
                                                type="number"
                                                value={maxStorageCapacity}
                                                disabled={config?.is_frozen}
                                                onChange={(e) => setMaxStorageCapacity(e.target.value === "" ? "" : Number(e.target.value))}
                                                className="h-9 px-3 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-blue-500 bg-white"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Interactive Augmentation Schedule Table */}
                            {config?.simulation_type === "closed_loop" && (
                                <div className="border border-slate-200 rounded-2xl p-5 bg-white shadow-sm space-y-4 mt-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-3">
                                        <div>
                                            <h4 className="font-bold text-[#0052CC] text-xs uppercase tracking-wider">
                                                Interactive Augmentation Schedule
                                            </h4>
                                            <p className="text-[11px] text-slate-500 mt-0.5">
                                                Edit augmentation years and container quantities below to re-run and compare updated results.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleUpdateAndReRun}
                                            disabled={updatingAugmentation || config?.is_frozen}
                                            className="h-9 px-4 bg-[#0052cc] hover:bg-[#0043a4] text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed self-start sm:self-auto"
                                            title={config?.is_frozen ? "Simulation is frozen" : ""}
                                        >
                                            {updatingAugmentation ? <LoadingOutlined /> : <SyncOutlined />} Re-run Simulation
                                        </button>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse text-xs">
                                            <thead>
                                                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                                    <th className="py-2.5 px-3">Augmentation</th>
                                                    <th className="py-2.5 px-3">Project Year</th>
                                                    <th className="py-2.5 px-3">Container Quantity</th>
                                                    <th className="py-2.5 px-3 text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {editablePurchaseYears.map((item, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-50/50">
                                                        <td className="py-2.5 px-3 font-bold text-slate-700">
                                                            Augmentation #{idx + 1}
                                                        </td>
                                                        <td className="py-2.5 px-3">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-slate-400 font-semibold text-[10px]">Year</span>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max={config?.simulation_tenure || 50}
                                                                    value={item.year ?? ""}
                                                                    disabled={config?.is_frozen}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value === "" ? "" : Number(e.target.value);
                                                                        const next = [...editablePurchaseYears];
                                                                        next[idx] = { ...next[idx], year: val };
                                                                        setEditablePurchaseYears(next);
                                                                    }}
                                                                    className="h-8 w-24 px-2.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-blue-500 bg-white shadow-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="py-2.5 px-3">
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    value={item.containers ?? ""}
                                                                    disabled={config?.is_frozen}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value === "" ? "" : Number(e.target.value);
                                                                        const next = [...editablePurchaseYears];
                                                                        next[idx] = { ...next[idx], containers: val };
                                                                        setEditablePurchaseYears(next);
                                                                    }}
                                                                    className="h-8 w-24 px-2.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-blue-500 bg-white shadow-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                                                                />
                                                                <span className="text-slate-400 font-semibold text-[10px]">Ctrs</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-2.5 px-3 text-center">
                                                            <button
                                                                type="button"
                                                                disabled={config?.is_frozen}
                                                                onClick={() => {
                                                                    const next = editablePurchaseYears.filter((_, i) => i !== idx);
                                                                    setEditablePurchaseYears(next);
                                                                }}
                                                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg border-none inline-flex items-center justify-center cursor-pointer transition-colors shadow-none disabled:opacity-30 disabled:cursor-not-allowed"
                                                                title={config?.is_frozen ? "Simulation is frozen" : "Remove Entry"}
                                                            >
                                                                <DeleteOutlined style={{ fontSize: "14px" }} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {editablePurchaseYears.length === 0 && (
                                                    <tr>
                                                        <td colSpan="4" className="py-4 text-center text-slate-400 text-xs italic">
                                                            No augmentation years defined. Click below to add an augmentation entry.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="flex justify-between items-center pt-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                let nextYear = 2;
                                                if (editablePurchaseYears.length > 0) {
                                                    const lastYear = Number(editablePurchaseYears[editablePurchaseYears.length - 1]?.year);
                                                    if (!isNaN(lastYear) && lastYear > 0) {
                                                        nextYear = lastYear + 1;
                                                    }
                                                }
                                                setEditablePurchaseYears([...editablePurchaseYears, { year: nextYear, containers: 1 }]);
                                            }}
                                            className="h-8 px-3.5 border border-dashed border-[#0052cc] text-[#0052cc] hover:bg-blue-50/50 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer bg-white"
                                        >
                                            <PlusOutlined /> Add Augmentation Entry
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Simulation Results Plots & Table */}
                <div className="space-y-6 mt-6">
                    {results.summary?.years_below_contract && results.summary.years_below_contract.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-sm font-semibold flex flex-col gap-2 shadow-sm animate-fade-in">
                            <span className="text-red-700 font-bold flex items-center gap-1.5">
                                <WarningOutlined className="text-red-500" />
                                Diagnostics Alert: Contract Breach Detected
                            </span>
                            <span className="text-xs text-red-600 font-medium">
                                POI delivered energy was lower than contracted target in years:{" "}
                                {results.summary.years_below_contract.map((yr) => `Year ${yr}`).join(", ")}
                            </span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                        <div className="flex flex-col bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                            <PoiDeliveredChart
                                data={results.year_outputs}
                                targetVal={parseFloat(config.energy_poi_mwh)}
                            />
                        </div>
                        <div className="flex flex-col bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                            <AugmentationChart
                                data={results.year_outputs}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                        <div className="lg:col-span-12 bg-white border border-slate-100 rounded-2xl p-5 shadow-sm h-full flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                                <h5 className="text-[14px] font-bold text-slate-800">
                                    Year-by-Year Simulation Outputs
                                </h5>
                                <button
                                    type="button"
                                    onClick={handleDownloadExcel}
                                    className="px-3.5 py-1.5 bg-[#f0fdfa] text-emerald-700 hover:bg-[#ccfbf1] border border-emerald-200 rounded-lg font-semibold text-xs cursor-pointer transition-all shadow-sm flex items-center gap-1.5"
                                >
                                    <DownloadOutlined /> Export Excel
                                </button>
                            </div>
                            <div className="w-full border border-slate-100 rounded-xl overflow-auto max-h-[650px] scrollbar-thin">
                                <table className="w-full text-left border-collapse min-w-[2800px] bess-table">
                                    <thead>
                                        <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                            {cohortsToRender.map(cItem => (
                                                <React.Fragment key={cItem.index}>
                                                    <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">{cItem.header}</th>
                                                    <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">MWh</th>
                                                </React.Fragment>
                                            ))}
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">Total Container</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">Year</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-right sticky top-0 bg-slate-50 z-10">DC installed capacity</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">Calander degradation for 6 months at 25 deg C</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">SOH (%)</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">Y-O-Y degradation</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">DOD</th>
                                            {cohortsToRender.map(cItem => {
                                                let colName = "";
                                                if (cItem.index === 0) {
                                                    colName = "DC Usable capacity@ Container terminals (MWh)";
                                                } else {
                                                    colName = `DC Usable capacity@Aumendation-${cItem.index} (MWh)`;
                                                }
                                                return (
                                                    <th key={cItem.index} className="py-3 px-3.5 border-r border-slate-100 text-right sticky top-0 bg-slate-50 z-10">{colName}</th>
                                                );
                                            })}
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-right sticky top-0 bg-slate-50 z-10">Total DC Usable capacity (MWh)</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">DC Discharge Efficiency (%)</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">DC RTE (%)</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">DC Charge Efficiency (%)</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-right sticky top-0 bg-slate-50 z-10">Aux Power Losses (MWh)</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-right sticky top-0 bg-slate-50 z-10">Usable Power after Aux power consumption (MWh)</th>
                                            <th className="py-3 px-3.5 border-r border-slate-100 text-center sticky top-0 bg-slate-50 z-10">Total % losses till POI ( DC disccharge + System losses + Unavailbility)</th>
                                            <th className="py-3 px-3.5 text-right sticky top-0 bg-slate-50 z-10">Battery power to Grid (POI) MWh</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600">
                                        {results.year_outputs.map((yr) => {
                                            const isBreached = parseFloat(yr.active_fleet.poi_margin_mwh) < 0;
                                            const projectYear = yr.project_year;
                                            const y = projectYear - 1;
                                            const perf = performances.find(p => p.year === y);

                                            const activeAfterAux = parseFloat(yr.active_fleet.after_aux_mwh);
                                            const totalLossesPct = activeAfterAux > 0
                                                ? (parseFloat(yr.active_fleet.poi_mwh) / activeAfterAux) * 100
                                                : 0;

                                            return (
                                                <tr key={projectYear} className={`text-xs border-b border-slate-100 hover:bg-slate-50/80 transition-colors ${isBreached ? "bg-red-50/30 hover:bg-red-50/50" : ""
                                                    }`}>
                                                    {/* Cohort Columns */}
                                                    {cohortsToRender.map(cItem => {
                                                        if (!cItem.exists) {
                                                            return (
                                                                <React.Fragment key={cItem.index}>
                                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center">—</td>
                                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center">—</td>
                                                                </React.Fragment>
                                                            );
                                                        }
                                                        const age = projectYear - cItem.cohort.purchase_project_year;
                                                        if (age < 0) {
                                                            return (
                                                                <React.Fragment key={cItem.index}>
                                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center"></td>
                                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center"></td>
                                                                </React.Fragment>
                                                            );
                                                        }
                                                        const containersVal = cItem.cohort.containers_purchased;
                                                        const mwhVal = containersVal * ratedCapacity;
                                                        return (
                                                            <React.Fragment key={cItem.index}>
                                                                <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">{containersVal}</td>
                                                                <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">{mwhVal.toFixed(2)}</td>
                                                            </React.Fragment>
                                                        );
                                                    })}

                                                    {/* Total Container */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-semibold text-slate-800">{yr.active_fleet.containers}</td>

                                                    {/* Year */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 font-semibold text-slate-800 text-center">{projectYear}</td>

                                                    {/* DC installed capacity */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-right font-medium">{parseFloat(yr.active_fleet.dc_installed_mwh).toFixed(2)}</td>

                                                    {/* Calendar degradation */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">
                                                        {perf ? `${parseFloat(perf.calander_degradation).toFixed(2)}%` : "100.00%"}
                                                    </td>

                                                    {/* SOH (%) */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">
                                                        {perf ? `${parseFloat(perf.soh).toFixed(2)}%` : "—"}
                                                    </td>

                                                    {/* Y-O-Y degradation */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">
                                                        {perf ? `${parseFloat(perf.yoy_degradation).toFixed(2)}%` : "—"}
                                                    </td>

                                                    {/* DOD */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">
                                                        {perf ? `${parseFloat(perf.dod).toFixed(2)}%` : "100.00%"}
                                                    </td>

                                                    {/* Usable capacities */}
                                                    {cohortsToRender.map(cItem => {
                                                        if (!cItem.exists) {
                                                            return (
                                                                <td key={cItem.index} className="py-2.5 px-3.5 border-r border-slate-100 text-right font-medium">—</td>
                                                            );
                                                        }
                                                        const age = projectYear - cItem.cohort.purchase_project_year;
                                                        if (age < 0) {
                                                            return (
                                                                <td key={cItem.index} className="py-2.5 px-3.5 border-r border-slate-100 text-right font-medium"></td>
                                                            );
                                                        }
                                                        const cPerf = performances.find(p => p.year === age);
                                                        const cFactor = getDegradationFactor(cPerf);
                                                        const usableCap = cItem.cohort.containers_purchased * ratedCapacity * cFactor;
                                                        return (
                                                            <td key={cItem.index} className="py-2.5 px-3.5 border-r border-slate-100 text-right font-medium">{usableCap.toFixed(5)}</td>
                                                        );
                                                    })}

                                                    {/* Total DC Usable capacity (MWh) */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-right font-semibold text-slate-800">{parseFloat(yr.active_fleet.terminals_mwh).toFixed(5)}</td>

                                                    {/* DC Discharge Efficiency (%) */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">
                                                        {perf ? `${parseFloat(perf.discharge_efficiency).toFixed(2)}%` : "—"}
                                                    </td>

                                                    {/* DC RTE (%) */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">
                                                        {perf ? `${parseFloat(perf.dc_rte).toFixed(2)}%` : "—"}
                                                    </td>

                                                    {/* DC Charge Efficiency (%) */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">
                                                        {perf ? `${parseFloat(perf.charge_efficiency).toFixed(2)}%` : "—"}
                                                    </td>

                                                    {/* Aux Power Losses (MWh) */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-right font-medium text-red-500">{parseFloat(yr.active_fleet.aux_mwh).toFixed(2)}</td>

                                                    {/* Usable Power after Aux */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-right font-medium">{activeAfterAux.toFixed(2)}</td>

                                                    {/* Total % losses till POI */}
                                                    <td className="py-2.5 px-3.5 border-r border-slate-100 text-center font-medium">{totalLossesPct.toFixed(2)}%</td>

                                                    {/* Battery power to Grid (POI) MWh */}
                                                    <td className="py-2.5 px-3.5 text-right font-semibold text-slate-800">{parseFloat(yr.active_fleet.poi_mwh).toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Capacity & System Losses Heatmaps */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                        <div className="flex flex-col bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                            <SohComplianceHeatmap
                                data={results.year_outputs}
                                targetVal={parseFloat(config.energy_poi_mwh)}
                            />
                        </div>
                        <div className="flex flex-col bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                            <SystemLossHeatmap
                                data={results.year_outputs}
                            />
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Hidden printable A4 report container */}
            <div id="bess-report-printable-area" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
                <style>{`
                    .bess-report-page {
                        background-color: #ffffff;
                        width: 794px;
                        height: 1123px;
                        padding: 50px 60px;
                        box-sizing: border-box;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        font-family: 'Inter', sans-serif;
                        color: #1e293b;
                    }
                    .bess-report-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        border-bottom: 2px solid #0052cc;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    .bess-report-title {
                        font-family: 'Inter', sans-serif;
                        font-size: 24px;
                        font-weight: 800;
                        color: #0c2340;
                        margin: 0;
                    }
                    .bess-report-section {
                        margin-bottom: 24px;
                    }
                    .bess-report-section-title {
                        font-family: 'Inter', sans-serif;
                        font-size: 14px;
                        font-weight: 700;
                        color: #0052cc;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        border-bottom: 1px solid #e2e8f0;
                        padding-bottom: 6px;
                        margin-bottom: 14px;
                    }
                    .bess-report-grid {
                        display: grid;
                        grid-template-cols: repeat(2, 1fr);
                        gap: 12px 24px;
                        font-size: 11px;
                    }
                    .bess-report-item {
                        display: flex;
                        justify-content: space-between;
                        border-bottom: 1px dashed #f1f5f9;
                        padding-bottom: 4px;
                    }
                    .bess-report-label {
                        color: #64748b;
                        font-weight: 500;
                    }
                    .bess-report-value {
                        color: #0f172a;
                        font-weight: 700;
                    }
                    .bess-report-footer {
                        border-top: 1px solid #e2e8f0;
                        padding-top: 10px;
                        display: flex;
                        justify-content: space-between;
                        font-size: 9px;
                        color: #94a3b8;
                        font-weight: 500;
                    }
                `}</style>

                {/* Page 1: Input Summary */}
                <div id="bess-report-page-1" className="bess-report-page">
                    <div>
                        <div className="bess-report-header">
                            <h2 className="bess-report-title">BESS Sizing Simulation Report</h2>
                        </div>

                        <div className="bess-report-section" style={{ marginTop: 10 }}>
                            <div className="bess-report-section-title">Run Details</div>
                            <div className="bess-report-grid">
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Project Name</span>
                                    <span className="bess-report-value">{config?.name || `Simulation #${config?.id}`}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Run Date</span>
                                    <span className="bess-report-value">{results?.ran_at ? new Date(results.ran_at).toLocaleString() : "—"}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Simulation Type</span>
                                    <span className="bess-report-value">
                                        {config?.simulation_type === "closed_loop"
                                            ? "Manual Battery Augmentation"
                                            : config?.simulation_type === "auto_closed_loop"
                                            ? "Auto Battery Augmentation"
                                            : "Without Battery Augmentation"}
                                    </span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Simulation Tenure</span>
                                    <span className="bess-report-value">{config?.simulation_tenure} Years</span>
                                </div>
                            </div>
                        </div>

                        <div className="bess-report-section">
                            <div className="bess-report-section-title">System Specifications</div>
                            <div className="bess-report-grid">
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Battery Container OEM</span>
                                    <span className="bess-report-value">{selectedContainer?.oem || "—"}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Container Capacity</span>
                                    <span className="bess-report-value">{selectedContainer ? `${parseFloat(selectedContainer.rated_capacity_mwh).toFixed(3)} MWh` : "—"}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">PCS OEM</span>
                                    <span className="bess-report-value">{selectedPCS?.oem || "—"}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">PCS Model</span>
                                    <span className="bess-report-value">{selectedPCS?.model || "—"}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Initial Container Qty</span>
                                    <span className="bess-report-value">{config?.container_nos} Ctrs</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Initial PCS Qty</span>
                                    <span className="bess-report-value">{config?.pcs_nos} Nos</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Storage Duration</span>
                                    <span className="bess-report-value">{config?.operation_hour} Hours</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Commence Year</span>
                                    <span className="bess-report-value">{config?.start_year}</span>
                                </div>
                                <div className="bess-report-item col-span-2">
                                    <span className="bess-report-label">Contracted Energy @ Grid POI</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.energy_poi_mwh).toFixed(2) : "—"} MWh</span>
                                </div>
                                {config?.simulation_type === "closed_loop" && (
                                    <div className="bess-report-item col-span-2">
                                        <span className="bess-report-label">Augmentation Years</span>
                                        <span className="bess-report-value">
                                            {Array.isArray(config?.purchase_years) && config.purchase_years.length > 0
                                                ? config.purchase_years.map((item) => {
                                                    if (typeof item === "object" && item !== null) {
                                                        const yr = item.year !== undefined ? item.year : item.purchase_year;
                                                        const ctrs = item.containers !== undefined ? item.containers : item.count;
                                                        return ctrs && Number(ctrs) > 0 ? `Yr ${yr} (${ctrs} Ctr${Number(ctrs) > 1 ? "s" : ""})` : `Yr ${yr}`;
                                                    }
                                                    return `Yr ${item}`;
                                                }).join(", ")
                                                : "None"}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bess-report-section">
                            <div className="bess-report-section-title">System Losses Settings</div>
                            <div className="bess-report-grid">
                                {config?.use_220kv_loss !== false && (
                                    <div className="bess-report-item">
                                        <span className="bess-report-label">{config?.tl_volt_stage_1 ?? 220} kV Transmission Line Loss</span>
                                        <span className="bess-report-value">{config ? parseFloat(config.transmission_line_220kv_loss ?? config.transmission_line_loss ?? 0).toFixed(3) : "—"}%</span>
                                    </div>
                                )}
                                {config?.use_400kv_loss === true && (
                                    <div className="bess-report-item">
                                        <span className="bess-report-label">{config?.tl_volt_stage_2 ?? 400} kV Transmission Line Loss</span>
                                        <span className="bess-report-value">{config ? parseFloat(config.transmission_line_400kv_loss ?? config.transformer_400kv_loss ?? 0).toFixed(3) : "—"}%</span>
                                    </div>
                                )}
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Solar Aux Power</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.solar_aux_power).toFixed(3) : "—"}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">{config?.tl_volt_stage_1 ?? 220}/33 kV Transformer Loss</span>
                                    <span className="bess-report-value">
                                        {config
                                            ? ((config.transformer_220_loss ?? config.transformer_220kv_loss) != null
                                                ? parseFloat(config.transformer_220_loss ?? config.transformer_220kv_loss).toFixed(3)
                                                : (config.pss_transformer_loss ? parseFloat(config.pss_transformer_loss).toFixed(3) : "0.000"))
                                            : "—"
                                        }%
                                    </span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">{config?.tl_volt_stage_2 ?? 400}/{config?.tl_volt_stage_1 ?? 220} kV Transformer Loss</span>
                                    <span className="bess-report-value">
                                        {config
                                            ? ((config.transformer_400_220_loss ?? config.transformer_400_loss ?? config.transformer_400kv_loss) != null
                                                ? parseFloat(config.transformer_400_220_loss ?? config.transformer_400_loss ?? config.transformer_400kv_loss).toFixed(3)
                                                : "0.000")
                                            : "—"
                                        }%
                                    </span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">High Tension AC Cable Loss</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.ht_ac_cable_loss).toFixed(3) : "—"}%</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Inverter Duty Transformer Loss</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.idt_loss).toFixed(3) : "—"}%</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Low Tension AC Cable Loss</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.lt_ac_cable_loss).toFixed(3) : "—"}%</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Power Conversion System Loss</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.pcs_loss).toFixed(3) : "—"}%</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Low Tension DC Cable Loss</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.lt_dc_cable_loss).toFixed(3) : "—"}%</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">BESS Plant Availability</span>
                                    <span className="bess-report-value">{config ? parseFloat(config.bess_plant_unavailability).toFixed(3) : "—"}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy BESS Sizing Module</span>
                        <span>Page 1 of 5</span>
                    </div>
                </div>

                {/* Page 2: Battery Degradation Table & Graph */}
                <div id="bess-report-page-2" className="bess-report-page" style={{ padding: '40px 50px' }}>
                    <div>
                        <div className="bess-report-header" style={{ marginBottom: '10px' }}>
                            <h2 className="bess-report-title">BESS Simulation Graphs</h2>
                        </div>

                        <div className="bess-report-section" style={{ marginBottom: '10px' }}>
                            <div className="bess-report-section-title" style={{ marginBottom: '6px' }}>Battery Degradation Summary</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8fafc' }}>
                                        <th style={{ border: '1px solid #cbd5e1', padding: '4px 6px', fontWeight: 'bold', color: '#1e293b' }}>End of Year</th>
                                        <th style={{ border: '1px solid #cbd5e1', padding: '4px 6px', fontWeight: 'bold', color: '#1e293b' }}>State of Health (%)</th>
                                        <th style={{ border: '1px solid #cbd5e1', padding: '4px 6px', fontWeight: 'bold', color: '#1e293b' }}>Battery Installed Energy Capacity (kWh)</th>
                                        <th style={{ border: '1px solid #cbd5e1', padding: '4px 6px', fontWeight: 'bold', color: '#1e293b' }}>Battery DC/DC RTE (%)</th>
                                        <th style={{ border: '1px solid #cbd5e1', padding: '4px 6px', fontWeight: 'bold', color: '#1e293b' }}>Battery POI Energy Capacity (kWh)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {degradationTableRows.map((row, idx) => (
                                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                                            <td style={{ border: '1px solid #cbd5e1', padding: '3px 5px', fontWeight: 'bold' }}>{row.year}</td>
                                            <td style={{ border: '1px solid #cbd5e1', padding: '3px 5px' }}>{row.soh.toFixed(1)}%</td>
                                            <td style={{ border: '1px solid #cbd5e1', padding: '3px 5px' }}>{Math.round(row.installed).toLocaleString()}</td>
                                            <td style={{ border: '1px solid #cbd5e1', padding: '3px 5px' }}>{row.rte.toFixed(2)}%</td>
                                            <td style={{ border: '1px solid #cbd5e1', padding: '3px 5px' }}>{Math.round(row.poi).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px', marginTop: '10px' }}>
                            <BatteryDegradationChart data={sohData} />
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy BESS Sizing Module</span>
                        <span>Page 2 of 5</span>
                    </div>
                </div>

                {/* Page 3: POI Delivered & Augmentation Charts */}
                <div id="bess-report-page-3" className="bess-report-page">
                    <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginTop: '10px' }}>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                                <PoiDeliveredChart data={results?.year_outputs} targetVal={config ? parseFloat(config.energy_poi_mwh) : 0} />
                            </div>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                                <AugmentationChart data={results?.year_outputs} />
                            </div>
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy BESS Sizing Module</span>
                        <span>Page 3 of 5</span>
                    </div>
                </div>

                {/* Page 4: SOH & Compliance Heatmap */}
                <div id="bess-report-page-4" className="bess-report-page">
                    <div>
                        <div className="bess-report-header">
                            <h2 className="bess-report-title">Heatmap Analysis</h2>
                        </div>

                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginTop: '20px' }}>
                            <SohComplianceHeatmap data={results?.year_outputs} targetVal={config ? parseFloat(config.energy_poi_mwh) : 0} isPrint={true} />
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy BESS Sizing Module</span>
                        <span>Page 4 of 5</span>
                    </div>
                </div>

                {/* Page 5: System Losses Heatmap */}
                <div id="bess-report-page-5" className="bess-report-page">
                    <div>
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginTop: '20px' }}>
                            <SystemLossHeatmap data={results?.year_outputs} isPrint={true} />
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy BESS Sizing Module</span>
                        <span>Page 5 of 5</span>
                    </div>
                </div>
            </div>
        </div >
    );
}

function PoiDeliveredChart({ data, targetVal }) {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const height = 260;
    const width = 500;
    const paddingLeft = 45;
    const paddingRight = 20;
    const paddingTop = 20;
    const paddingBottom = 40;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    if (!data || data.length === 0) return null;

    const values = data.flatMap(d => [parseFloat(d.active_fleet.poi_mwh) || 0, parseFloat(d.contracted_poi_mwh) || 0]);
    const minVal = 0;
    const maxVal = Math.max(...values, targetVal * 1.1) || 120;

    const getX = (index) => {
        return paddingLeft + (index + 0.5) * (chartWidth / data.length);
    };

    const getY = (val) => {
        return paddingTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
    };

    const targetY = getY(targetVal);
    const targetPath = `M ${paddingLeft} ${targetY} L ${width - paddingRight} ${targetY}`;

    const yTicks = [
        0,
        Math.round(maxVal * 0.25),
        Math.round(maxVal * 0.5),
        Math.round(maxVal * 0.75),
        Math.round(maxVal)
    ];

    const barWidth = Math.max(4, (chartWidth / data.length) * 0.6);

    return (
        <div className="relative bg-white p-2 h-full flex flex-col justify-between">
            <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
                <span>POI Energy vs Contract Target (MWh)</span>
                <div className="flex gap-3 text-[9px] font-semibold">
                    <span className="flex items-center gap-1 text-red-500">
                        <span className="w-2.5 h-0.5 bg-red-500 inline-block border-t border-red-500 border-dashed" />
                        Target ({targetVal.toFixed(1)} MWh)
                    </span>
                    <span className="flex items-center gap-1 text-sky-500">
                        <span className="w-3.5 h-2 bg-sky-500 rounded-sm inline-block" />
                        POI Delivered
                    </span>
                </div>
            </h5>

            <svg viewBox={`0 0 ${width} ${height}`} className="w-full flex-1 min-h-[180px]">
                {yTicks.map((tick, i) => {
                    const y = getY(tick);
                    return (
                        <g key={i} className="opacity-40">
                            <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
                            <text x={paddingLeft - 8} y={y + 3} textAnchor="end" className="text-[8px] font-bold fill-slate-400">{tick}</text>
                        </g>
                    );
                })}

                {data.map((d, i) => {
                    const x = getX(i);
                    return (
                        <text key={i} x={x} y={height - paddingBottom + 16} textAnchor="middle" className="text-[7.5px] font-bold fill-slate-400">{d.project_year}</text>
                    );
                })}

                {/* Render bars for POI Delivered */}
                {data.map((d, i) => {
                    const x = getX(i);
                    const val = parseFloat(d.active_fleet?.poi_mwh || 0);
                    if (val <= 0) return null;

                    const barHeight = (val / maxVal) * chartHeight;
                    const y = getY(val);

                    return (
                        <rect
                            key={i}
                            x={x - barWidth / 2}
                            y={y}
                            width={barWidth}
                            height={barHeight}
                            fill={hoveredIndex === i ? "#0ea5e9" : "#0284c7"}
                            rx="2"
                            className="transition-all duration-200"
                        />
                    );
                })}

                {/* Target Line */}
                <path d={targetPath} fill="none" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="5 5" />

                {/* Hover zones */}
                {data.map((d, i) => {
                    const x = getX(i);
                    return (
                        <g key={i}>
                            <rect
                                x={x - (chartWidth / data.length) / 2}
                                y={paddingTop}
                                width={chartWidth / data.length}
                                height={chartHeight}
                                fill="transparent"
                                className="cursor-pointer"
                                onMouseEnter={() => setHoveredIndex(i)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            />
                            {hoveredIndex === i && (
                                <>
                                    <line x1={x} y1={paddingTop} x2={x} y2={height - paddingBottom} stroke="#94a3b8" strokeWidth="1" strokeDasharray="2 2" />
                                </>
                            )}
                        </g>
                    );
                })}
            </svg>

            {hoveredIndex !== null && data[hoveredIndex] && (
                <div className="absolute top-[65px] left-[55px] bg-slate-900/90 text-white rounded-lg p-2 text-[9px] space-y-0.5 shadow-lg pointer-events-none backdrop-blur-sm z-30 min-w-[120px]">
                    <div className="font-bold text-slate-300 border-b border-slate-700 pb-0.5 mb-1">Project Year {data[hoveredIndex].project_year}</div>
                    <div>POI: <span className="font-bold text-sky-400">{parseFloat(data[hoveredIndex].active_fleet.poi_mwh).toFixed(2)} MWh</span></div>
                    <div>Target: <span className="font-bold text-red-400">{parseFloat(data[hoveredIndex].contracted_poi_mwh).toFixed(2)} MWh</span></div>
                    <div>Margin: <span className={`font-bold ${parseFloat(data[hoveredIndex].active_fleet.poi_margin_mwh) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{parseFloat(data[hoveredIndex].active_fleet.poi_margin_mwh).toFixed(2)} MWh</span></div>
                </div>
            )}
        </div>
    );
}

function AugmentationChart({ data }) {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const height = 260;
    const width = 500;
    const paddingLeft = 45;
    const paddingRight = 20;
    const paddingTop = 20;
    const paddingBottom = 40;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    if (!data || data.length === 0) return null;

    const values = data.map(d => parseInt(d.augmented_fleet?.newly_purchased || 0, 10));
    const maxVal = Math.max(...values, 5) || 5;

    const getX = (index) => {
        return paddingLeft + (index + 0.5) * (chartWidth / data.length);
    };

    const getY = (val) => {
        return paddingTop + chartHeight - (val / maxVal) * chartHeight;
    };

    const yTicks = [
        0,
        Math.round(maxVal * 0.25),
        Math.round(maxVal * 0.5),
        Math.round(maxVal * 0.75),
        Math.round(maxVal)
    ];

    const barWidth = Math.max(4, (chartWidth / data.length) * 0.6);

    return (
        <div className="relative bg-white p-2 h-full flex flex-col justify-between">
            <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
                <span>Augmentation Schedule (Containers Purchased)</span>
                <div className="flex gap-2 text-[9px] font-semibold">
                    <span className="flex items-center gap-1 text-amber-500">
                        <span className="w-3.5 h-2 bg-amber-500 rounded-sm inline-block" />
                        Containers Added
                    </span>
                </div>
            </h5>

            <svg viewBox={`0 0 ${width} ${height}`} className="w-full flex-1 min-h-[180px]">
                {yTicks.map((tick, i) => {
                    const y = getY(tick);
                    return (
                        <g key={i} className="opacity-40">
                            <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
                            <text x={paddingLeft - 8} y={y + 3} textAnchor="end" className="text-[8px] font-bold fill-slate-400">{tick}</text>
                        </g>
                    );
                })}

                {data.map((d, i) => {
                    const x = getX(i);
                    return (
                        <text key={i} x={x} y={height - paddingBottom + 16} textAnchor="middle" className="text-[7.5px] font-bold fill-slate-400">{d.project_year}</text>
                    );
                })}

                {/* Render bars for years with augmentation */}
                {data.map((d, i) => {
                    const x = getX(i);
                    const qty = parseInt(d.augmented_fleet?.newly_purchased || 0, 10);
                    if (qty <= 0) return null;

                    const barHeight = (qty / maxVal) * chartHeight;
                    const y = getY(qty);

                    return (
                        <rect
                            key={i}
                            x={x - barWidth / 2}
                            y={y}
                            width={barWidth}
                            height={barHeight}
                            fill="#f59e0b"
                            rx="2"
                            className="transition-all duration-200"
                        />
                    );
                })}

                {/* Hover zones */}
                {data.map((d, i) => {
                    const x = getX(i);
                    return (
                        <g key={i}>
                            <rect
                                x={x - (chartWidth / data.length) / 2}
                                y={paddingTop}
                                width={chartWidth / data.length}
                                height={chartHeight}
                                fill="transparent"
                                className="cursor-pointer"
                                onMouseEnter={() => setHoveredIndex(i)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            />
                            {hoveredIndex === i && (
                                <>
                                    <line x1={x} y1={paddingTop} x2={x} y2={height - paddingBottom} stroke="#94a3b8" strokeWidth="1" strokeDasharray="2 2" />
                                </>
                            )}
                        </g>
                    );
                })}
            </svg>

            {hoveredIndex !== null && data[hoveredIndex] && (
                <div className="absolute top-[65px] left-[55px] bg-slate-900/90 text-white rounded-lg p-2 text-[9px] space-y-0.5 shadow-lg pointer-events-none backdrop-blur-sm z-30 min-w-[120px]">
                    <div className="font-bold text-slate-300 border-b border-slate-700 pb-0.5 mb-1">Project Year {data[hoveredIndex].project_year}</div>
                    <div>Newly Purchased: <span className="font-bold text-amber-400">{parseInt(data[hoveredIndex].augmented_fleet?.newly_purchased || 0, 10)} Containers</span></div>
                    <div>Total Active Ctrs: <span className="font-bold text-slate-300">{parseInt(data[hoveredIndex].active_fleet?.containers || 0, 10)} Containers</span></div>
                </div>
            )}
        </div>
    );
}

function BatteryDegradationChart({ data }) {
    const height = 220;
    const width = 500;
    const paddingLeft = 45;
    const paddingRight = 20;
    const paddingTop = 20;
    const paddingBottom = 40;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    if (!data || data.length === 0) return null;

    const minYear = 0;
    const maxYear = data.length - 1;

    const sohValues = data.map(d => d.soh);
    const rawMinVal = Math.min(...sohValues);

    // Round down the minimum value to the nearest multiple of 5
    const minVal = Math.max(0, Math.floor(rawMinVal / 5) * 5);
    const maxVal = 100;

    const getX = (index) => {
        return paddingLeft + (index / maxYear) * chartWidth;
    };

    const getY = (val) => {
        return paddingTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
    };

    const points = data.map((d, i) => ({ x: getX(i), y: getY(d.soh) }));
    const path = points.reduce((acc, curr, index) => `${acc}${index === 0 ? "M" : "L"} ${curr.x} ${curr.y}`, "");

    // Generate ticks from minVal to 100 with steps of 5% or 10%
    const tickStep = (100 - minVal) > 30 ? 10 : 5;
    const yTicks = [];
    for (let t = minVal; t <= 100; t += tickStep) {
        yTicks.push(t);
    }

    return (
        <div className="relative bg-white p-1 h-full flex flex-col justify-between" style={{ fontFamily: 'Inter, sans-serif' }}>
            <h5 className="text-[12px] font-bold text-slate-700 mb-2 flex items-center justify-between">
                <span>Battery Degradation</span>
                <div className="flex gap-2 text-[9px] font-semibold">
                    <span className="flex items-center gap-1 text-red-500">
                        <span className="w-2.5 h-0.5 bg-red-500 inline-block" />
                        SoH (%)
                    </span>
                </div>
            </h5>

            <svg viewBox={`0 0 ${width} ${height}`} className="w-full flex-1 min-h-[160px]">
                {yTicks.map((tick, i) => {
                    const y = getY(tick);
                    return (
                        <g key={i} className="opacity-40">
                            <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
                            <text x={paddingLeft - 8} y={y + 3} textAnchor="end" className="text-[8px] font-bold fill-slate-400">{tick}%</text>
                        </g>
                    );
                })}

                {data.map((d, i) => {
                    const x = getX(i);
                    return (
                        <text key={i} x={x} y={height - paddingBottom + 16} textAnchor="middle" className="text-[7.5px] font-bold fill-slate-400">{d.year}</text>
                    );
                })}

                {/* SoH Degradation Line */}
                <path d={path} fill="none" stroke="#EF4444" strokeWidth="2.5" />
            </svg>
        </div>
    );
}

function SohComplianceHeatmap({ data, targetVal, isPrint = false }) {
    if (!data || data.length === 0) return null;

    const initialDC = parseFloat(data[0].initial_plant?.dc_installed_mwh) || 1;

    const getCellColor = (colKey, val) => {
        if (colKey === "installed") {
            if (val >= 105) return { bg: "#065f46", text: "#ffffff" };
            if (val >= 100) return { bg: "#047857", text: "#ffffff" };
            return { bg: "#10b981", text: "#ffffff" };
        }
        if (val >= 98) return { bg: "#047857", text: "#ffffff" };
        if (val >= 94) return { bg: "#10b981", text: "#ffffff" };
        if (val >= 90) return { bg: "#6ee7b7", text: "#064e3b" };
        if (val >= 85) return { bg: "#fef08a", text: "#713f12" };
        if (val >= 80) return { bg: "#fed7aa", text: "#7c2d12" };
        if (val >= 70) return { bg: "#f97316", text: "#ffffff" };
        return { bg: "#ef4444", text: "#ffffff" };
    };

    const columns = [
        { key: "installed", label: "DC Installed" },
        { key: "soh", label: "Terminal SOH" },
        { key: "aux", label: "After AUX" },
        { key: "poi", label: "Net POI" },
        { key: "compliance", label: "Compliance" }
    ];

    return (
        <div className="bg-white p-3 h-full flex flex-col justify-between" style={{ fontFamily: 'Inter, sans-serif' }}>
            <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
                <span>SOH & Contract Target Compliance Heatmap</span>
            </h5>
            <div className={isPrint ? "border border-slate-100 rounded-xl" : "overflow-auto border border-slate-100 rounded-xl max-h-[400px]"}>
                <table className="w-full text-center border-collapse text-[10px]">
                    <thead>
                        <tr className={isPrint ? "bg-slate-50 border-b border-slate-200" : "bg-slate-50 border-b border-slate-200 sticky top-0 z-10"}>
                            <th className="py-2 px-1 text-slate-500 font-bold border-r border-slate-100">Year</th>
                            {columns.map(col => (
                                <th key={col.key} className="py-2 px-1 text-slate-500 font-bold border-r border-slate-100">{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {data.map(yr => {
                            const installedPct = (parseFloat(yr.active_fleet.dc_installed_mwh) / initialDC) * 100;
                            const sohPct = (parseFloat(yr.active_fleet.terminals_mwh) / parseFloat(yr.active_fleet.dc_installed_mwh)) * 100;
                            const auxPct = (parseFloat(yr.active_fleet.after_aux_mwh) / parseFloat(yr.active_fleet.dc_installed_mwh)) * 100;
                            const poiPct = (parseFloat(yr.active_fleet.poi_mwh) / parseFloat(yr.active_fleet.dc_installed_mwh)) * 100;
                            const compliancePct = (parseFloat(yr.active_fleet.poi_mwh) / parseFloat(yr.contracted_poi_mwh)) * 100;

                            const rowData = {
                                installed: installedPct,
                                soh: sohPct,
                                aux: auxPct,
                                poi: poiPct,
                                compliance: compliancePct
                            };

                            return (
                                <tr key={yr.project_year} className="hover:bg-slate-50/50">
                                    <td className="py-1.5 px-2 font-bold text-slate-700 border-r border-slate-100 bg-slate-50">Yr {yr.project_year}</td>
                                    {columns.map(col => {
                                        const val = rowData[col.key];
                                        const { bg, text } = getCellColor(col.key, val);
                                        return (
                                            <td key={col.key} className="p-1 border-r border-slate-100" style={{ backgroundColor: bg, color: text, fontWeight: '600' }}>
                                                {val.toFixed(1)}%
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Color Legend */}
            <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-[9px] font-semibold text-slate-500 justify-center">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#047857" }} /> &ge; 98%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#10b981" }} /> 94% - 98%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#6ee7b7" }} /> 90% - 94%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#fef08a" }} /> 85% - 90%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#fed7aa" }} /> 80% - 85%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#f97316" }} /> 70% - 80%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#ef4444" }} /> &lt; 70%</span>
            </div>
        </div>
    );
}

function SystemLossHeatmap({ data, isPrint = false }) {
    if (!data || data.length === 0) return null;

    const getCellColor = (val) => {
        if (val === 0) return { bg: "#f8fafc", text: "#64748b" };
        if (val <= 2) return { bg: "#fee2e2", text: "#991b1b" };
        if (val <= 5) return { bg: "#fca5a5", text: "#7f1d1d" };
        if (val <= 8) return { bg: "#f87171", text: "#ffffff" };
        if (val <= 12) return { bg: "#ef4444", text: "#ffffff" };
        return { bg: "#b91c1c", text: "#ffffff" };
    };

    const columns = [
        { key: "aux", label: "Auxiliary Loss" },
        { key: "bos", label: "BOS Loss" },
        { key: "total", label: "Total Loss" },
        { key: "shortfall", label: "Shortfall Margin" }
    ];

    return (
        <div className="bg-white p-3 h-full flex flex-col justify-between" style={{ fontFamily: 'Inter, sans-serif' }}>
            <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
                <span>System Losses Contribution Heatmap</span>
            </h5>
            <div className={isPrint ? "border border-slate-100 rounded-xl" : "overflow-auto border border-slate-100 rounded-xl max-h-[400px]"}>
                <table className="w-full text-center border-collapse text-[10px]">
                    <thead>
                        <tr className={isPrint ? "bg-slate-50 border-b border-slate-200" : "bg-slate-50 border-b border-slate-200 sticky top-0 z-10"}>
                            <th className="py-2 px-1 text-slate-500 font-bold border-r border-slate-100">Year</th>
                            {columns.map(col => (
                                <th key={col.key} className="py-2 px-1 text-slate-500 font-bold border-r border-slate-100">{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {data.map(yr => {
                            const dcInstalled = parseFloat(yr.active_fleet.dc_installed_mwh) || 1;
                            const auxPct = (parseFloat(yr.active_fleet.aux_mwh) / dcInstalled) * 100;
                            const bosPct = (parseFloat(yr.active_fleet.bos_loss_mwh) / dcInstalled) * 100;
                            const totalPct = auxPct + bosPct;
                            const margin = parseFloat(yr.active_fleet.poi_margin_mwh);
                            const shortfallPct = margin < 0 ? (Math.abs(margin) / parseFloat(yr.contracted_poi_mwh)) * 100 : 0;

                            const rowData = {
                                aux: auxPct,
                                bos: bosPct,
                                total: totalPct,
                                shortfall: shortfallPct
                            };

                            return (
                                <tr key={yr.project_year} className="hover:bg-slate-50/50">
                                    <td className="py-1.5 px-2 font-bold text-slate-700 border-r border-slate-100 bg-slate-50">Yr {yr.project_year}</td>
                                    {columns.map(col => {
                                        const val = rowData[col.key];
                                        const { bg, text } = getCellColor(val);
                                        return (
                                            <td key={col.key} className="p-1 border-r border-slate-100" style={{ backgroundColor: bg, color: text, fontWeight: '600' }}>
                                                {val.toFixed(1)}%
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Color Legend */}
            <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-[9px] font-semibold text-slate-500 justify-center">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#f8fafc", border: '1px solid #cbd5e1' }} /> 0% (No Loss)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#fee2e2" }} /> &le; 2%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#fca5a5" }} /> 2% - 5%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#f87171" }} /> 5% - 8%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#ef4444" }} /> 8% - 12%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#b91c1c" }} /> &gt; 12%</span>
            </div>
        </div>
    );
}
